// Recording a clip, on a device somebody is holding. Lifted from the Andon
// app's `useRecorder`; the four sentences it can say are handed in as words.
//
// One hook for audio and video, because the only thing that differs is which
// tracks are requested and which container the browser will hand back — and
// two copies of "ask for the stream, start, tick, stop, release the tracks"
// is how a released microphone stops being released on one of the two paths.
//
// THE TRACKS ARE RELEASED ON EVERY EXIT
//
// Not just on stop. The recording indicator in a phone's status bar is the
// most alarming thing this app can leave switched on, and an operator who sees
// it stays lit after they put the phone down does not use the feature again.
// So: stop, error, unmount, and cancel all run through `release`.
//
// THE STREAM IS EXPOSED. Video recording used to say "Recording video" and
// nothing else, so an operator was pointing a phone at a machine and guessing.
// The stream reaches a `<video>` element now, and the caller draws it.
//
// TORCH AND LENS FLIP LIVE HERE TOO, because both are operations on the TRACK
// rather than on the element, and a caller reaching into `stream.getTracks()`
// to apply constraints would be a caller that can leave a torch on after
// `release` has run.

import { useCallback, useEffect, useRef, useState } from 'react';
import { canRecord, extensionFor, pickMimeType } from '@/lib/capture';

export interface RecorderWords {
  cannotRecord: string;
  micRefused: string;
  cameraRefused: string;
  couldNotStartAudio: string;
  couldNotStartVideo: string;
}

export interface Recorder {
  supported: boolean;
  recording: boolean;
  /** Whole seconds so far, for the timer beside the waveform. */
  seconds: number;
  error: string | null;
  start: () => Promise<void>;
  /** Stop and hand back what was recorded. Null if nothing usable was. */
  stop: () => Promise<{ blob: Blob; name: string; seconds: number } | null>;
  /** Stop and throw it away — the back-out that leaves nothing behind. */
  cancel: () => void;

  // ── the viewfinder's half ────────────────────────────────────────────────
  /**
   * The live tracks, for a `<video>` to show. Null when not recording.
   *
   * Held in state rather than only in a ref so the caller re-renders when it
   * arrives: a ref mutation is invisible to React, and a viewfinder that only
   * appears on the next unrelated render is a viewfinder that appears seconds
   * late or not at all.
   */
  stream: MediaStream | null;
  /** How long a clip may run, so the caller can print the real limit. */
  maxSeconds: number;
  /**
   * Whether this camera can be told to switch its lamp on.
   *
   * False on desktop, false on iOS Safari, and false on plenty of Android
   * hardware — the capability is queried rather than assumed, because a torch
   * button that does nothing is worse than no torch button.
   */
  torchAvailable: boolean;
  torchOn: boolean;
  toggleTorch: () => Promise<void>;
  /** Which way the camera points, and a way to turn it round. */
  facing: 'environment' | 'user';
  flipCamera: () => Promise<void>;
}

/**
 * Hard stop, so a pocketed phone cannot record for an hour.
 *
 * VIDEO IS SIXTY SECONDS, AUDIO IS THREE MINUTES, and the difference is not an
 * oversight. A voice note is somebody explaining a fault and three minutes of it
 * is still worth listening to. A video is evidence of a machine doing something
 * wrong, and past a minute it stops being evidence and becomes footage nobody
 * scrubs through — while costing tens of megabytes on a factory uplink to send.
 */
const MAX_SECONDS: Record<'audio' | 'video', number> = { audio: 180, video: 60 };

/**
 * What the camera is ASKED for. `ideal`, never `exact`.
 *
 * `exact` fails outright on hardware that cannot do it, which on a floor of
 * mixed Zebra tablets and personal phones means the recorder simply refuses to
 * open on some of them. `ideal` gets 1080p where it exists and the best
 * available everywhere else, which is the correct trade for a clip whose job is
 * to make a rack label readable.
 */
const VIDEO_CONSTRAINTS = { width: { ideal: 1920 }, height: { ideal: 1080 } };

export function useRecorder(kind: 'audio' | 'video', words: RecorderWords): Recorder {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<MediaStream | null>(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef(0);

  const release = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    for (const track of stream.current?.getTracks() ?? []) track.stop();
    stream.current = null;
    recorder.current = null;
    setRecording(false);
    // The exposed copy goes too, or the caller keeps a `<video>` pointed at
    // stopped tracks — a frozen last frame that looks exactly like a working
    // viewfinder.
    setLive(null);
    // Stopping the track puts the lamp out, so the flag has to follow or the
    // button comes back lit on the next recording with the torch off.
    setTorchOn(false);
    setTorchAvailable(false);
  }, []);

  // Unmounting mid-recording is the case a person creates by tapping Back, and
  // it is exactly when they expect the light to go out.
  useEffect(() => release, [release]);

  const cap = MAX_SECONDS[kind];

  /**
   * Open the tracks, for a given lens.
   *
   * Separate from `start` because the lens flip needs exactly this and nothing
   * else: new tracks, same recorder. Folding it into `start` would mean a flip
   * that restarts the clip, which throws away whatever has been filmed so far.
   */
  const openTracks = useCallback(
    async (want: 'environment' | 'user'): Promise<MediaStream> => {
      const media = await navigator.mediaDevices.getUserMedia(
        kind === 'audio' ? { audio: true } : { audio: true, video: { facingMode: want, ...VIDEO_CONSTRAINTS } },
      );
      stream.current = media;
      setLive(media);
      // ASKED, NOT ASSUMED. `torch` is not in the standard capability set, so
      // this is a cast rather than a type — and the whole reason to query it is
      // that a torch button which does nothing reads as a broken app.
      const track = media.getVideoTracks()[0];
      const caps = track?.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorchAvailable(Boolean(caps?.torch));
      setTorchOn(false);
      return media;
    },
    [kind],
  );

  const start = useCallback(async () => {
    if (recording) return;
    setError(null);
    if (!canRecord()) {
      setError(words.cannotRecord);
      return;
    }
    try {
      // Back to the rear lens every time. A flip is about THIS clip — the next
      // andon is a different machine, and the camera that matters for it is the
      // one pointing away from the operator.
      setFacing('environment');
      const media = await openTracks('environment');
      const mimeType = pickMimeType(kind);
      const rec = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      recorder.current = rec;
      startedAt.current = Date.now();
      setSeconds(0);
      rec.start();
      setRecording(true);
      timer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt.current) / 1000);
        setSeconds(elapsed);
        // The recorder stops itself rather than relying on somebody
        // remembering. See MAX_SECONDS for why video's cap is much tighter
        // than audio's.
        if (elapsed >= cap && recorder.current?.state === 'recording') recorder.current.stop();
      }, 250);
    } catch (err) {
      release();
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? kind === 'audio'
            ? words.micRefused
            : words.cameraRefused
          : kind === 'audio'
            ? words.couldNotStartAudio
            : words.couldNotStartVideo,
      );
    }
  }, [cap, kind, openTracks, recording, release, words]);

  /**
   * The lamp.
   *
   * Chrome on Android and essentially nowhere else, which is why
   * `torchAvailable` gates the control rather than this function refusing after
   * the fact. Failures are swallowed on purpose: the clip is still recording,
   * and an error toast about a lamp on top of a live viewfinder is noise at the
   * worst moment. What the operator sees is the frame not getting brighter,
   * which is the honest signal.
   */
  const toggleTorch = useCallback(async () => {
    const track = stream.current?.getVideoTracks()[0];
    if (!track || !torchAvailable) return;
    const next = !torchOn;
    try {
      // `torch` is real on Chrome for Android and absent from the TypeScript DOM
      // types, so the cast goes through `unknown`.
      await track.applyConstraints({ advanced: [{ torch: next }] } as unknown as MediaTrackConstraints);
      setTorchOn(next);
    } catch {
      setTorchOn(false);
    }
  }, [torchAvailable, torchOn]);

  /**
   * Turn the camera round, WITHOUT restarting the clip.
   *
   * The old video track is stopped and a new one from the other lens is put in
   * its place on the same MediaStream, so the MediaRecorder keeps writing into
   * the same file. Restarting instead would silently discard whatever had been
   * filmed, which on a fault that just happened is the one thing that cannot be
   * repeated.
   *
   * Audio is untouched — flipping the lens mid-sentence must not cut the words.
   */
  const flipCamera = useCallback(async () => {
    const media = stream.current;
    if (kind !== 'video' || !media) return;
    const want = facing === 'environment' ? 'user' : 'environment';
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({ video: { facingMode: want, ...VIDEO_CONSTRAINTS } });
      const incoming = fresh.getVideoTracks()[0];
      if (!incoming) {
        for (const t of fresh.getTracks()) t.stop();
        return;
      }
      for (const old of media.getVideoTracks()) {
        media.removeTrack(old);
        old.stop();
      }
      media.addTrack(incoming);
      setFacing(want);
      const caps = incoming.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorchAvailable(Boolean(caps?.torch));
      setTorchOn(false);
      // A new MediaStream identity, so a `<video srcObject=…>` actually
      // re-attaches. Mutating the tracks of the same object does not always
      // make the element repaint.
      setLive(new MediaStream(media.getTracks()));
    } catch {
      // The lens the device does not have. Nothing is lost — the original track
      // is still recording, because it is only removed once a replacement is
      // in hand.
    }
  }, [facing, kind]);

  const stop = useCallback(async (): Promise<{ blob: Blob; name: string; seconds: number } | null> => {
    const rec = recorder.current;
    if (!rec || rec.state === 'inactive') {
      release();
      return null;
    }
    const elapsed = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
    const type = rec.mimeType || pickMimeType(kind);
    const blob = await new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(chunks.current, type ? { type } : undefined));
      rec.stop();
    });
    release();
    if (blob.size === 0) return null;
    return { blob, name: `${kind === 'audio' ? 'voice' : 'clip'}.${extensionFor(type)}`, seconds: elapsed };
  }, [kind, release]);

  const cancel = useCallback(() => {
    const rec = recorder.current;
    chunks.current = [];
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      rec.stop();
    }
    release();
    setSeconds(0);
  }, [release]);

  return {
    supported: canRecord(),
    recording,
    seconds,
    error,
    start,
    stop,
    cancel,
    stream: live,
    maxSeconds: cap,
    torchAvailable,
    torchOn,
    toggleTorch,
    facing,
    flipCamera,
  };
}
